import './sell-list.scss'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import CommonModal from '@/components/CommonModal/CommonModal'
import { useStore } from '@/store'
import { useLocal } from '@/i18n/LocalContext'
import { getSellData } from '@app/request'
import appExpose from '@app/expose'
import { Handler } from 'mitt'
import { SellData } from '@app/store/sell'
import { useMaxWidth } from '@/hooks/useMaxWidth'
import { createPlayerName, getColorStyle } from '@app/utils'
import { useSell } from '@app/hooks/useSell'
import { $alert } from '@/utils'
import Tooltips from '@/components/Tooltips/Tooltips'

const { emitter } = appExpose

export default function SellList() {
  const { $t } = useLocal()
  const { state, dispatch } = useStore()
  const { sell } = useSell()
  const sellListOpen = state.sellListModalOpen
  const playerId = state.login.userInfo.playerId
  const sellData = state.sellData
  const { safeAreaTop } = useMaxWidth()
  const coinStyle = useMemo(() => {
    const style = { transform: `translateY(${safeAreaTop}px)` }
    return style
  }, [safeAreaTop])
  const [current, setCurrent] = useState<SellData['list'][number] | null>(null)
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(pending)
  const pendingStyle = useMemo(
    () => ({
      display: pending ? 'block' : 'none',
    }),
    [pending]
  )
  const tableStyle = useMemo(
    () => ({
      opacity: pending ? 0 : 1,
    }),
    [pending]
  )
  const [cd, setCD] = useState(false)
  const sellBtnClassnameList = useMemo(
    () => [
      `sell-btn${cd ? ' sell-btn--disabled' : ''}`,
      // `sell-btn${!current ? ' sell-btn--disabled' : ''}`,
      'sell-btn',
    ],
    [cd, current]
  )
  /** CD 60秒 */
  const cdTotal = 30
  const [cdTimeout, setCDTimeout] = useState(cdTotal)
  const cdText = useMemo(
    () =>
      $t('恢复倒计时：%{time}').replace(
        '%{time}',
        `<span>${String(cdTimeout)}S</span>`
      ),
    [cdTimeout]
  )
  const cdTextStyle = useMemo(
    () => (cd ? { opacity: '1' } : { opacity: '0' }),
    [cd]
  )

  const fetchSellListData = useCallback(
    async (refresh = false) => {
      if (pendingRef.current) return

      pendingRef.current = true
      setPending(pendingRef.current)
      const data = await getSellData({ playerId, refresh })
      dispatch({ type: 'setState', data: { sellData: data } })
      pendingRef.current = false
      setCurrent(null)
      setPending(pendingRef.current)
    },
    [playerId]
  )

  const refresh = useCallback(() => {
    if (cd) {
      console.log('冷卻中...')
      return
    }

    setCD(true)
    fetchSellListData(true)
  }, [cd, fetchSellListData])
  useEffect(() => {
    if (!cd) return

    console.log('開始冷卻...')
    let n = 0
    const timer = window.setInterval(() => {
      n += 1
      if (n === cdTotal + 1) {
        setCD(false)
        console.log('冷卻結束')
        n = 0
        window.clearInterval(timer)
      }
      setCDTimeout(cdTotal - n)
    }, 1000)
  }, [cd])

  useEffect(() => {
    const _handle: Handler<MittEvents['updateSellListData']> = (data) => {
      if (data.refresh) {
        refresh()
      } else {
        fetchSellListData()
      }
    }
    emitter.on('updateSellListData', _handle)

    return () => {
      emitter.off('updateSellListData', _handle)
    }
  }, [fetchSellListData, refresh])

  const onClose = () => {
    dispatch({ type: 'setState', data: { sellListModalOpen: false } })
  }

  const handleSelect = useCallback((item: SellData['list'][number]) => {
    if (item.overage <= 0) {
      return
    }

    setCurrent(item)
  }, [])

  const handleSell = useCallback(() => {
    if (!current) {
      $alert($t('请选择收购商出售'))
      return
    }

    sell('market', {
      list: [current],
      tree: sellData.tree,
    })
  }, [current, sellData, sell])

  const createRowClassName = useCallback(
    (item: SellData['list'][number]) => {
      let className = 'sell-table-row'
      if (item.overage <= 0) {
        className += ' sell-table-row--disabled'
      }
      if (current && current.uid === item.uid) {
        className += ' sell-table-row--current'
      }
      return className
    },
    [current]
  )

  const createCount = (item: SellData['list'][number]) => {
    if (item.overage <= 0) {
      return $t('已售罄')
    }
    // return `${item.overage}/${item.total}`
    return `${item.overage}`
  }

  return (
    <CommonModal
      open={sellListOpen}
      onClose={onClose}
      title={$t('市场出售')}
      type="sell-list"
    >
      {[
        sellData.list.length ? (
          <div key="1" className="sell-list">
            <div className="__onemt__loader-box" style={pendingStyle}>
              <div className="__onemt__loader"></div>
            </div>
            <ul className="sell-table-header" style={tableStyle}>
              <li className="sell-table-header-item">
                <span>{$t('玩家名')}</span>
              </li>
              <li className="sell-table-header-item">
                <span>{$t('集市收购价')}</span>
              </li>
              <li className="sell-table-header-item">
                <span>{$t('剩余收购数量')}</span>
              </li>
            </ul>
            <div className="sell-table-body" style={tableStyle}>
              <div className="sell-table-body__inner">
                {sellData.list.map((item, index) => (
                  <ul
                    className={createRowClassName(item)}
                    key={index}
                    onClick={() => handleSelect(item)}
                  >
                    <li className="sell-table-cell">
                      <div className="sell-table-item sell-table-item--break">
                        <span>{createPlayerName(item)}</span>
                      </div>
                    </li>
                    <li className="sell-table-cell">
                      <div className="sell-table-item sell-table-item--break">
                        <span
                          className="sell-table-item__coin"
                          style={getColorStyle(item)}
                        >
                          {item.price}
                        </span>
                      </div>
                    </li>
                    <li className="sell-table-cell">
                      <div className="sell-table-item sell-table-item--break">
                        <span>{createCount(item)}</span>
                      </div>
                    </li>
                  </ul>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div key="11" className="christmas-modal-hints">
            {$t('暂未查到玩家集市，请点击左下方【刷新】按钮重新查询')}
          </div>
        ),
        [
          <div key="111" className="sell-btn-list">
            <div className="sell-btn-block">
              <div className={sellBtnClassnameList[0]} onClick={refresh}>
                <span>{$t('刷新')}</span>
              </div>
            </div>
            <div className="sell-btn-block">
              <div className={sellBtnClassnameList[1]} onClick={handleSell}>
                <span>{$t('出售')}</span>
              </div>
            </div>
            <div className="coin-block" style={coinStyle}>
              <Tooltips
                title={$t('圣诞树')}
                content={$t('圣诞集市活动道具，可在圣诞集市活动中进行售卖')}
                place="right-start"
                ckey="sellistcointree"
                className="coin tree"
              >
                <span>{sellData.tree}</span>
              </Tooltips>
            </div>
          </div>,
          <p
            key="222"
            className="refresh-cd"
            style={cdTextStyle}
            dangerouslySetInnerHTML={{ __html: cdText }}
          ></p>,
        ],
      ]}
    </CommonModal>
  )
}
